import { Card, useDialog } from "@fpsoftware/mvp";
import type { ComponentProps } from "react";
import { TbEdit } from "react-icons/tb";
import { type Address, formatMoney, getAddressLabel } from "shared";
import { useSuspenseAddress } from "@/addresses/hooks";
import { UpdateAddressTaxDialog } from "@/addresses/UpdateAddressTaxDialog";
import { UpdateAddressValueDialog } from "@/addresses/UpdateAddressValueDialog";
import { Button } from "@/components/Button";
import { EditableValue } from "@/components/EditableValue/EditableValue";

interface AddressCardProps extends ComponentProps<typeof Card> {
  addressID: number;
  onEdit?: () => void;
}

function formatRange(range: Address["valueRange"]) {
  if (!range) {
    return null;
  }

  return `${formatMoney(range[0], 0)} - ${formatMoney(range[1], 0)}`;
}

export function AddressCard(props: AddressCardProps) {
  const { addressID, onEdit, ...rest } = props;
  const address = useSuspenseAddress(addressID);
  const { open } = useDialog();

  const handleTaxDialogOpen = () => {
    open(UpdateAddressTaxDialog, {
      addressID: address.ID,
    });
  };

  const handleValueDialogOpen = () => {
    open(UpdateAddressValueDialog, {
      addressID: address.ID,
    });
  };

  const range = formatRange(address.valueRange);

  return (
    <Card {...rest}>
      <Card.Header>
        Property
        {onEdit && (
          <Button size="sm" icon={TbEdit} onClick={onEdit}>
            Edit
          </Button>
        )}
      </Card.Header>
      <Card.List>
        <Card.ListItem label="Address">
          {getAddressLabel(address)}
        </Card.ListItem>
        <Card.ListItem label="Annual Property Tax">
          <EditableValue
            label={formatMoney(address.taxes?.amount)}
            sublabel={address.taxes ? `for ${address.taxes.year}` : undefined}
            onEdit={handleTaxDialogOpen}
          />
        </Card.ListItem>
        <Card.ListItem label="Estimated Value">
          <EditableValue
            label={formatMoney(address.value, 0)}
            sublabel={range ?? undefined}
            onEdit={handleValueDialogOpen}
          />
        </Card.ListItem>
      </Card.List>
    </Card>
  );
}
